import FieldHint from '@/components/common/FieldHint';
import { Input, Select } from '@/components/ui/FormControls';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useConfirm } from '@/contexts/ConfirmContext';
import {
  useCatalogChoices,
  useCatalogProfile,
  useCatalogVersions,
  type CatalogDefinition,
  type CatalogField,
} from '@/hooks/useCatalog';
import { useOperation, useOperations } from '@/hooks/useOperations';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import CatalogFields, { catalogInput as input } from '@/components/products/CatalogFields';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import JerseyIdentifierSettings from '@/components/products/JerseyIdentifierSettings';
import JerseyEntrySettings from '@/components/products/JerseyEntrySettings';
import { cleanJerseyConfig, JERSEY_HELPER_CODES } from '@klothiq/domain';
import { useTeams } from '@/hooks/useMasterConfig';
const field = (
  code: string,
  label: string,
  type: CatalogField['type'] = 'text',
  options?: string[],
  level: CatalogField['level'] = 'product',
): CatalogField => ({
  code,
  label,
  type,
  level,
  required: false,
  ...(options ? { options: options.map((v) => ({ value: v, label: v })) } : {}),
});
const fieldTypeLabels: Record<CatalogField['type'], string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Yes / No',
  select: 'Choose one',
  multiselect: 'Choose multiple',
  date: 'Date',
};
function preset(name: string): CatalogDefinition {
  const common = [
    field('MATERIAL', 'Material'),
    field('SIZE', 'Size', 'text', undefined, 'variant'),
    field('COLOR', 'Colour', 'text', undefined, 'variant'),
  ];
  const sleeve = field('SLEEVE_TYPE', 'Sleeve', 'select', [
    'Half Sleeve',
    'Full Sleeve',
    'Sleeveless',
    '3/4 Sleeve',
  ]);
  const fields =
    name === 'Jersey'
      ? [
          field('TEAM', 'Team'),
          field('EDITION', 'Edition', 'select', ['Home', 'Away', 'Third', 'Training']),
          sleeve,
          field('COLLAR_TYPE', 'Collar'),
          field('PLAYER_NAME', 'Player name'),
          field('PLAYER_NUMBER', 'Player number'),
          ...common,
        ]
      : name === 'Helmet'
        ? [
            field('BRAND', 'Brand'),
            field('STYLE', 'Style', 'select', ['Full face', 'Open face', 'Modular', 'Off road']),
            field('CERTIFICATION', 'Certification'),
            ...common,
          ]
        : [
            sleeve,
            field('FIT', 'Fit', 'select', ['Regular Fit', 'Slim Fit', 'Oversized', 'Relaxed Fit']),
            field(
              name === 'Shirt' ? 'COLLAR_TYPE' : 'NECKLINE',
              name === 'Shirt' ? 'Collar' : 'Neckline',
            ),
            ...common,
          ];
  return {
    code_prefix: name === 'T-shirt' ? 'TSHIRT' : name.toUpperCase(),
    fields,
    category_ids: [],
  };
}
export default function CatalogConfigurationPage() {
  const teams = useTeams();
  const choices = useCatalogChoices();
  const ops = useOperations();
  const [id, setId] = useState('');
  const profile = useCatalogProfile(id);
  const versions = useCatalogVersions(id);
  const confirm = useConfirm();
  const [definition, setDefinition] = useState<CatalogDefinition>({
    fields: [],
    code_prefix: '',
    category_ids: [],
  });
  const [error, setError] = useState(''),
    [draft, setDraft] = useState<{ schema_version_id: string; version: number } | null>(null),
    [preview, setPreview] = useState(false);
  const baseVersion = useRef<string | null>(null);
  const loaded = useRef('');
  const lastOperation = useRef<'draft' | 'publish'>('draft');
  const save = useOperation<{ success: boolean; schema_version_id: string; version: number }>(
    'save_catalog_profile_draft_v1',
  );
  const publish = useOperation('publish_catalog_profile_v1');
  const intent = useOperationIntent(`catalog-${id}`);
  const busy = save.isPending || publish.isPending;
  useEffect(() => {
    if (profile.data && loaded.current !== `${id}:${profile.data.schema_version_id}`) {
      if (loaded.current.startsWith(`${id}:`)) {
        setError(
          'The published setup changed while this editor was open. Your draft is kept. Review the current setup before saving.',
        );
        return;
      }
      baseVersion.current = profile.data.schema_version_id;
      loaded.current = `${id}:${profile.data.schema_version_id}`;
      setDefinition({
        ...profile.data.definition,
        category_ids: profile.data.definition.category_ids || [],
      });
      setDraft(null);
      setError('');
    }
  }, [id, profile.data]);
  function edit(next: CatalogDefinition) {
    setDefinition(next);
    setDraft(null);
    setError('');
  }
  function update(index: number, patch: Partial<CatalogField>) {
    edit({
      ...definition,
      fields: definition.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    });
  }
  async function saveDraft() {
    lastOperation.current = 'draft';
    setError('');
    try {
      const r = await save.mutateAsync({
        profile_id: id,
        expected_current_schema_version_id: baseVersion.current,
        definition: {
          ...definition,
          ...(definition.jersey_identifiers
            ? { jersey_identifiers: cleanJerseyConfig(definition.jersey_identifiers) }
            : {}),
        },
        request_key: intent.key('draft'),
      });
      intent.complete('draft');
      setDraft(r);
      toast.success(`Draft version ${r.version} saved`);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function publishDraft() {
    if (
      !draft ||
      !(await confirm({
        title: 'Publish category setup?',
        description: `${profile.data?.product_count || 0} existing products retain their saved version and details. New products use this version. Review fields, required rules and available options before publishing.`,
        confirmText: 'Publish version',
      }))
    )
      return;
    lastOperation.current = 'publish';
    setError('');
    try {
      await publish.mutateAsync({
        profile_id: id,
        schema_version_id: draft.schema_version_id,
        expected_version: draft.version,
        request_key: intent.key('publish'),
      });
      intent.complete('publish');
      setDraft(null);
      const fresh = await profile.refetch();
      if (fresh.data) {
        setDefinition(fresh.data.definition);
        baseVersion.current = fresh.data.schema_version_id;
        loaded.current = `${id}:${fresh.data.schema_version_id}`;
      }
      setError('');
      toast.success('Category setup published');
    } catch (e) {
      setError((e as Error).message);
    }
  }
  if (choices.isLoading || ops.isLoading) return <LoadingState />;
  if (choices.error || ops.error)
    return (
      <ErrorState
        message={(choices.error || ops.error)!.message}
        onRetry={() => {
          void choices.refetch();
          void ops.refetch();
        }}
      />
    );
  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader
        title="Category setup"
        description="Configure product and variant fields. Published versions keep existing data readable."
        backTo="/master-config"
      />
      {ops.data?.modules.catalog !== 1 && (
        <p role="alert" className="rounded-xl bg-amber-500/10 p-4 text-sm">
          The catalog database migration is required before saving configuration.
        </p>
      )}
      <fieldset disabled={busy || ops.data?.modules.catalog !== 1} className="space-y-5">
        <section className="glass-card space-y-3 p-4">
          <label className="block space-y-1 text-sm">
            Product category
            <Select
              className={input}
              value={id}
              onChange={async (e) => {
                const next = e.target.value;
                if (
                  id &&
                  !(await confirm({
                    title: 'Switch category?',
                    description:
                      'Unsaved field changes will be discarded. Saved drafts remain in history.',
                    confirmText: 'Switch category',
                  }))
                )
                  return;
                setId(next);
                setDraft(null);
                loaded.current = '';
              }}
              helpLabel="Product category"
            >
              <option value="">Choose category</option>
              {choices.data?.types
                .filter((t) => t.is_active)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>
            <FieldHint>
              Choose the product family whose entry fields you want to configure.
            </FieldHint>
          </label>
          <p className="text-xs text-muted-foreground">
            Add or rename categories using Product Categories in Master Configuration. No categories
            are selected automatically.
          </p>
        </section>
        {profile.isLoading && <LoadingState />}
        {profile.error && <ErrorState message={profile.error.message} onRetry={profile.refetch} />}
        {id && profile.data && (
          <>
            <JerseyEntrySettings
              key={id}
              definition={definition}
              onChange={edit}
              available={ops.data?.modules.jersey_entry === 2}
            />
            <JerseyIdentifierSettings
              value={definition.jersey_identifiers}
              teams={(teams.data || []).filter((t) => t.is_active)}
              available={ops.data?.modules.jersey_identifiers === 1}
              optionalEntry={definition.jersey_entry?.enabled === true}
              onChange={(jersey_identifiers) => edit({ ...definition, jersey_identifiers })}
            />
            <section className="glass-card space-y-4 p-4 sm:p-6">
              <label className="block space-y-1 text-sm">
                Load a saved version
                <Select
                  className={input}
                  value=""
                  onChange={async (e) => {
                    const row = versions.data?.find((v) => v.id === e.target.value);
                    if (
                      row &&
                      (await confirm({
                        title: 'Load saved version?',
                        description:
                          'This replaces your current field draft. Saved versions and products remain unchanged.',
                        confirmText: 'Load version',
                      }))
                    ) {
                      setDefinition(row.definition);
                      baseVersion.current = profile.data?.schema_version_id || null;
                      loaded.current = `${id}:${profile.data?.schema_version_id}`;
                      setDraft(
                        row.status === 'draft'
                          ? { schema_version_id: row.id, version: row.version }
                          : null,
                      );
                      setError('');
                    }
                  }}
                  helpLabel="Load a saved version"
                >
                  <option value="">
                    {versions.isLoading ? 'Loading versions…' : 'Choose saved version'}
                  </option>
                  {versions.data?.map((v) => (
                    <option key={v.id} value={v.id}>
                      Version {v.version} · {v.status}
                    </option>
                  ))}
                </Select>
                <FieldHint>
                  Loads a draft for review. Your current unsaved field changes are replaced only
                  after confirmation.
                </FieldHint>
              </label>
              {versions.error && (
                <ErrorState message={versions.error.message} onRetry={versions.refetch} />
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-semibold">
                  Fields ·{' '}
                  {profile.data.legacy
                    ? 'Existing configuration'
                    : `Published v${profile.data.version}`}
                </h2>
                <button
                  className="min-h-11 rounded-xl border border-border px-3 text-sm"
                  onClick={() => setPreview(!preview)}
                >
                  {preview ? 'Edit fields' : 'Preview fields'}
                </button>
              </div>
              <label className="block max-w-xs space-y-1 text-sm">
                Code prefix *
                <Input
                  className={input}
                  maxLength={12}
                  value={definition.code_prefix}
                  onChange={(e) =>
                    edit({
                      ...definition,
                      code_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                    })
                  }
                  helpLabel="Code prefix *"
                />
                <FieldHint>
                  Used for new generated product codes, e.g. JERSEY. Existing product codes stay
                  unchanged.
                </FieldHint>
              </label>
              <details className="rounded-xl border border-border p-3">
                <summary className="min-h-11 cursor-pointer py-2 text-sm">
                  Start from a suggested field set
                </summary>
                <p className="mb-2 text-xs text-muted-foreground">
                  A suggestion replaces this draft's fields after confirmation. Review it before
                  publishing.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Jersey', 'T-shirt', 'Shirt', 'Helmet'].map((name) => (
                    <button
                      key={name}
                      className="min-h-11 rounded-xl border border-border px-3 text-sm"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: `Use ${name} fields?`,
                            description:
                              'This replaces only the fields you are editing. Existing products and published versions are retained.',
                            confirmText: 'Use suggested fields',
                          })
                        )
                          edit({
                            ...preset(name),
                            category_ids: definition.category_ids,
                            jersey_identifiers: definition.jersey_identifiers,
                            jersey_entry: definition.jersey_entry,
                          });
                      }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </details>
              {preview ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Example layout. Values entered here are not saved.
                  </p>
                  <Preview
                    fields={definition.fields.filter((f) => f.level === 'product')}
                    label="Product details"
                  />
                  <Preview
                    fields={definition.fields.filter((f) => f.level === 'variant')}
                    label="Variant details"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {definition.fields.map((f, i) => (
                    <article key={i} className="space-y-3 rounded-xl border border-border p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <strong className="text-sm">Field {i + 1}</strong>
                        <div className="flex gap-2">
                          <button
                            disabled={i === 0}
                            className="min-h-11 px-2 text-sm disabled:opacity-40"
                            onClick={() => {
                              const a = [...definition.fields];
                              [a[i - 1], a[i]] = [a[i]!, a[i - 1]!];
                              edit({ ...definition, fields: a });
                            }}
                          >
                            Move up
                          </button>
                          <button
                            className="min-h-11 px-2 text-sm text-destructive"
                            onClick={() =>
                              edit({
                                ...definition,
                                fields: definition.fields.filter((_, n) => n !== i),
                              })
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <label className="text-sm">
                          Label
                          <Input
                            className={input}
                            value={f.label}
                            maxLength={100}
                            onChange={(e) => update(i, { label: e.target.value })}
                            helpLabel="Label"
                          />
                          <FieldHint>
                            Shown to the person entering a product, e.g. Sleeve type.
                          </FieldHint>
                        </label>
                        <label className="text-sm">
                          Stable field code
                          <Input
                            className={input}
                            value={f.code}
                            maxLength={64}
                            onChange={(e) =>
                              update(i, {
                                code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                              })
                            }
                            helpLabel="Stable field code"
                          />
                          <FieldHint>
                            A permanent key, e.g. SLEEVE_TYPE. Keep it unchanged once used.
                          </FieldHint>
                        </label>
                        <label className="text-sm">
                          Level
                          <Select
                            className={input}
                            value={f.level}
                            onChange={(e) =>
                              update(i, { level: e.target.value as CatalogField['level'] })
                            }
                            helpLabel="Level"
                          >
                            <option value="product">Product</option>
                            <option value="variant">Variant</option>
                          </Select>
                          <FieldHint>
                            Product: shared details. Variant: a separate value for each size or
                            colour.
                          </FieldHint>
                        </label>
                        <label className="text-sm">
                          Input
                          <Select
                            className={input}
                            value={f.type}
                            onChange={(e) =>
                              update(i, { type: e.target.value as CatalogField['type'] })
                            }
                            helpLabel="Input"
                          >
                            {['text', 'number', 'boolean', 'select', 'multiselect', 'date'].map(
                              (t) => (
                                <option key={t} value={t}>
                                  {fieldTypeLabels[t as CatalogField['type']]}
                                </option>
                              ),
                            )}
                          </Select>
                          <FieldHint>
                            Choose how people enter this value: text, number, Yes/No, choice list or
                            date.
                          </FieldHint>
                        </label>
                      </div>
                      <label className="flex min-h-11 items-center gap-2 text-sm">
                        <Input
                          type="checkbox"
                          className="h-5 w-5 accent-primary"
                          checked={f.required}
                          disabled={
                            definition.jersey_entry?.enabled === true &&
                            JERSEY_HELPER_CODES.has(f.code)
                          }
                          onChange={(e) => update(i, { required: e.target.checked })}
                          helpLabel="Required for new values"
                        />
                        Required for new values
                      </label>
                      {(f.type === 'select' || f.type === 'multiselect') && (
                        <OptionEditor
                          options={f.options || []}
                          onChange={(options) => update(i, { options })}
                        />
                      )}
                      <div className="grid gap-3 sm:grid-cols-3">
                        {f.type === 'number' &&
                          (['min', 'max'] as const).map((key) => (
                            <label key={key} className="text-sm">
                              {key === 'min' ? 'Minimum' : 'Maximum'}
                              <Input
                                className={input}
                                type="number"
                                value={f[key] ?? ''}
                                onChange={(e) =>
                                  update(i, {
                                    [key]:
                                      e.target.value === '' ? undefined : Number(e.target.value),
                                  })
                                }
                                helpLabel={key === 'min' ? 'Minimum' : 'Maximum'}
                              />
                              <FieldHint>
                                Optional numeric limit. Leave it blank for no limit; zero is valid.
                              </FieldHint>
                            </label>
                          ))}
                        {f.type === 'text' && (
                          <label className="text-sm">
                            Maximum characters
                            <Input
                              className={input}
                              type="number"
                              min={1}
                              max={1000}
                              value={f.maxLength ?? 1000}
                              onChange={(e) => update(i, { maxLength: Number(e.target.value) })}
                              helpLabel="Maximum characters"
                            />
                            <FieldHint>Text length limit, from 1 to 1,000 characters.</FieldHint>
                          </label>
                        )}
                        <label className="text-sm">
                          Show when
                          <Select
                            className={input}
                            value={f.when?.field || ''}
                            onChange={(e) =>
                              update(i, {
                                when: e.target.value
                                  ? { field: e.target.value, equals: '' }
                                  : undefined,
                              })
                            }
                            helpLabel="Show when"
                          >
                            <option value="">Always visible</option>
                            {definition.fields
                              .slice(0, i)
                              .filter((p) => p.level === f.level && p.type !== 'multiselect')
                              .map((p) => (
                                <option key={p.code} value={p.code}>
                                  {p.label}
                                </option>
                              ))}
                          </Select>
                          <FieldHint>
                            Choose an earlier field at the same level, or Always to show this field
                            without a condition.
                          </FieldHint>
                        </label>
                        {f.when && (
                          <label className="text-sm">
                            Equals
                            <Input
                              className={input}
                              value={String(f.when.equals)}
                              onChange={(e) => {
                                const parent = definition.fields.find(
                                  (p) => p.code === f.when!.field,
                                );
                                update(i, {
                                  when: {
                                    field: f.when!.field,
                                    equals:
                                      parent?.type === 'boolean'
                                        ? e.target.value === 'true'
                                        : parent?.type === 'number'
                                          ? Number(e.target.value)
                                          : e.target.value,
                                  },
                                });
                              }}
                              helpLabel="Equals"
                            />
                            <FieldHint>
                              The exact stored value that makes this field appear.
                            </FieldHint>
                          </label>
                        )}
                      </div>
                    </article>
                  ))}
                  <button
                    className="min-h-11 rounded-xl border border-border px-4 text-sm"
                    disabled={definition.fields.length >= 100}
                    onClick={() =>
                      edit({ ...definition, fields: [...definition.fields, field('', '')] })
                    }
                  >
                    + Add field
                  </button>
                </div>
              )}
            </section>
            <section className="glass-card space-y-3 p-4">
              <h2 className="font-semibold">Allowed subcategories / collections</h2>
              <p className="text-xs text-muted-foreground">
                Optional classification. An empty selection allows None only for new products.
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {choices.data?.categories
                  .filter((c) => c.is_active || definition.category_ids.includes(c.id))
                  .map((c) => (
                    <label key={c.id} className="flex min-h-11 items-center gap-3 text-sm">
                      <Input
                        type="checkbox"
                        className="h-5 w-5 accent-primary"
                        checked={definition.category_ids.includes(c.id)}
                        onChange={(e) =>
                          edit({
                            ...definition,
                            category_ids: e.target.checked
                              ? [...definition.category_ids, c.id]
                              : definition.category_ids.filter((k) => k !== c.id),
                          })
                        }
                        helpLabel="Allowed subcategory / collection"
                      />
                      {c.name}
                      {!c.is_active ? ' (inactive — remove from the new version)' : ''}
                    </label>
                  ))}
              </div>
            </section>
            <section className="obsidian-panel sticky bottom-0 space-y-3 rounded-xl border border-border bg-card p-4 shadow-lg">
              {error && (
                <div role="alert" className="space-y-2 text-sm text-destructive">
                  <p>{error}</p>
                  <button
                    className="min-h-11 underline"
                    onClick={async () => {
                      if (
                        !(await confirm({
                          title: 'Load current category setup?',
                          description:
                            'This replaces the fields currently being edited. Saved drafts and products remain available.',
                          confirmText: 'Load current setup',
                        }))
                      )
                        return;
                      const result = await profile.refetch();
                      if (result.data) {
                        setDefinition(result.data.definition);
                        setDraft(null);
                        baseVersion.current = result.data.schema_version_id;
                        loaded.current = `${id}:${result.data.schema_version_id}`;
                        setError('');
                      }
                    }}
                  >
                    Load current setup
                  </button>
                  <button
                    className="min-h-11 underline"
                    onClick={() =>
                      void intent
                        .recover(lastOperation.current)
                        .then(async (r) => {
                          if (!r) {
                            setError('No completed request found. Review and retry.');
                            return;
                          }
                          const result = await versions.refetch();
                          if (
                            lastOperation.current === 'draft' &&
                            r.schema_version_id &&
                            r.version
                          ) {
                            const row = result.data?.find((v) => v.id === r.schema_version_id);
                            if (!row)
                              throw new Error(
                                'Saved draft was recovered, but its fields could not load. Reload this page.',
                              );
                            setDefinition(row.definition);
                            setDraft({
                              schema_version_id: r.schema_version_id,
                              version: r.version,
                            });
                          } else {
                            const fresh = await profile.refetch();
                            if (fresh.data) {
                              setDefinition(fresh.data.definition);
                              setDraft(null);
                              baseVersion.current = fresh.data.schema_version_id;
                              loaded.current = `${id}:${fresh.data.schema_version_id}`;
                            }
                          }
                          setError('');
                          toast.success('Previous request recovered');
                        })
                        .catch((e) => setError(e.message))
                    }
                  >
                    Recover previous save
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Changing a stable code creates a different field. Previously saved values remain in
                product history.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  className="min-h-11 rounded-xl border border-border px-4 text-sm"
                  onClick={() => void saveDraft()}
                >
                  Save draft
                </button>
                <button
                  disabled={!draft}
                  className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-40"
                  onClick={() => void publishDraft()}
                >
                  {draft ? `Publish v${draft.version}` : 'Save a draft to publish'}
                </button>
              </div>
            </section>
          </>
        )}
      </fieldset>
    </div>
  );
}
function OptionEditor({
  options,
  onChange,
}: {
  options: NonNullable<CatalogField['options']>;
  onChange: (v: NonNullable<CatalogField['options']>) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Option values stay stable; labels can change in a new version. Deactivate options to
        preserve older selections.
      </p>
      {options.map((o, i) => (
        <div key={i} className="grid items-center gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <label className="space-y-1 text-xs text-muted-foreground">
            Saved value
            <Input
              aria-label={`Option ${i + 1} stable value`}
              placeholder="e.g. HALF_SLEEVE"
              className={input}
              value={o.value}
              onChange={(e) =>
                onChange(options.map((v, n) => (n === i ? { ...v, value: e.target.value } : v)))
              }
            />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            Display label
            <Input
              aria-label={`Option ${i + 1} label`}
              placeholder="e.g. Half sleeve"
              className={input}
              value={o.label}
              onChange={(e) =>
                onChange(options.map((v, n) => (n === i ? { ...v, label: e.target.value } : v)))
              }
            />
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <Input
              type="checkbox"
              checked={o.active !== false}
              onChange={(e) =>
                onChange(options.map((v, n) => (n === i ? { ...v, active: e.target.checked } : v)))
              }
              helpLabel="Active"
            />
            Active
          </label>
        </div>
      ))}
      <button
        className="min-h-11 text-sm text-primary"
        onClick={() => onChange([...options, { value: '', label: '' }])}
      >
        + Add option
      </button>
    </div>
  );
}
function Preview({ fields, label }: { fields: CatalogField[]; label: string }) {
  const [values, setValues] = useState({});
  return (
    <section className="space-y-3 rounded-xl border border-border p-3">
      <h3 className="text-sm font-semibold">{label}</h3>
      <CatalogFields
        fields={fields}
        values={values}
        onChange={setValues}
        prefix={label.replaceAll(' ', '-')}
      />
    </section>
  );
}
